/**
 * <park-board-heading> — the information board's pill heading, standing on its
 * own (spec `board-visual-language` FR-11; anton/forrest#1405).
 *
 * `park-rule-group` carries the same pill as the header OF a card: it rides up
 * over the card's top edge and owns the items below it. A page section that is
 * not a rules card — the home page's "Näin varaat" / "Metsän osat" blocks —
 * needs the same announced heading WITHOUT a card wrapped around it, so the
 * board's grouping model reaches the rest of the site without every surface
 * re-becoming a rules board.
 *
 * Deliberately renders the SAME `.board-group__badge` / `.board-group__title`
 * classes as the group header, so the badge, the pill and the uppercase display
 * title resolve from one definition (FR-2 / US-7) — only the pill's positioning
 * differs (`.board-heading`, no overhang, in ./css/park-rule-board.css).
 *
 * Attributes:
 *   - title="…"   the section heading (rendered as <h2>). Required to render.
 *   - icon="…"    icon name for the badge; unknown/missing → paw (FR-4).
 *
 * @dsSpec board-visual-language#FR-11 — the board's pill heading for a page section.
 */

import { ParkElement, defineElement, esc, html } from "./base";
import { boardIconSvg } from "./board-icons";

const TAG = "park-board-heading";

export interface ParkBoardHeadingProps {
	title?: string;
	icon?: string;
}

/** Pure render: props → inner HTML string. No DOM access (server + client safe). */
export function renderParkBoardHeading(props: ParkBoardHeadingProps): string {
	const title = (props.title ?? "").trim();
	if (!title) return "";
	return html`<span class="board-heading"><span class="board-group__badge">${boardIconSvg(props.icon)}</span><h2 class="board-group__title">${esc(title)}</h2></span>`;
}

class ParkBoardHeading extends ParkElement {
	static get observedAttributes(): string[] {
		return ["title", "icon"];
	}

	protected render(): string {
		return renderParkBoardHeading({
			title: this.attr("title"),
			icon: this.attr("icon"),
		});
	}
}

defineElement(TAG, ParkBoardHeading);

export { ParkBoardHeading };
